import React, { useContext, useEffect, useMemo } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TkDatePickerProps } from "../../../type/componentsType/TkDatePicker";
import "./_style.scss";
import { TkFormContext } from "../../../context/tkFormContext";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";
import "dayjs/locale/ja";

/**
 * 日付選択部品
 * DatePickerを使用して日付を選択する
 * @param {string} label ラベル
 * @param {boolean} error
 * @param {string} errorMessage エラーメッセージ
 * @param {boolean} disabled trueの場合、非活性。falseの場合、活性。
 * @returns TkDatePicker 日付部品
 */
export const TkDatePicker: React.FC<TkDatePickerProps> = ({
  ref,
  label,
  onChange,
  error,
  errorMessage,
  disabled,
  wantToSetValue,
  minDate,
  maxDate,
  onGetInputValue,
  onGetErrorFlg,
  format = "YYYY / MM / DD",
  ...rest
}) => {
  const [value, setValue] = React.useState<Dayjs | null>(null);
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const { formContext, setFormContext } = useContext(TkFormContext);

  dayjs.locale("ja");

  const memoizedValue = useMemo(() => {
    return wantToSetValue ? dayjs(wantToSetValue) : null;
  }, [wantToSetValue]);

  useEffect(() => {
    setValue(memoizedValue || null);

    if (memoizedValue) {
      const defaultValue = {
        target: {
          value: memoizedValue,
        },
      };

      onGetInputValue && onGetInputValue(defaultValue, "isDefault");
      handleChange && handleChange(dayjs(memoizedValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedValue]);

  const handleChange = (newValue: Dayjs | null) => {
    let errorFlag = true;
    if (
      newValue &&
      (dayjs(newValue).isBefore(dayjs("1900/01/01")) ||
        !dayjs(newValue).isValid())
    ) {
      setErrorMsg(TkPropertyMessageGet("TKMSG_SCR0050E", "1900/01/01"));
    } else if (
      newValue &&
      !dayjs(newValue)
        .format("YYYY/MM/DD")
        .match(/^\d{4}\/\d{2}\/\d{2}$/)
    ) {
      setErrorMsg(TkPropertyMessageGet("TKMSG_SCR0049E"));
    } else if (
      newValue &&
      minDate &&
      dayjs(newValue).isBefore(dayjs(minDate))
    ) {
      setErrorMsg(
        TkPropertyMessageGet(
          "TKMSG_SCR0050E",
          dayjs(minDate).format("YYYY / MM / DD")
        )
      );
    } else if (newValue && maxDate && dayjs(newValue).isAfter(dayjs(maxDate))) {
      setErrorMsg(
        TkPropertyMessageGet(
          "TKMSG_SCR0051E",
          dayjs(maxDate).format("YYYY / MM / DD")
        )
      );
    } else {
      errorFlag = false;
      setErrorMsg("");
    }

    if (errorFlag) {
      setFormContext &&
        setFormContext({
          preventSubmit: {
            ...formContext?.preventSubmit,
            datePicker: true,
          },
        });
    } else {
      if (formContext?.preventSubmit?.datePicker)
        setFormContext?.({
          preventSubmit: {
            ...formContext.preventSubmit,
            datePicker: false,
          },
        });
    }

    setValue(newValue);
    const value = {
      target: {
        value: newValue?.format("YYYY/MM/DD"),
      },
    };

    onGetInputValue && onGetInputValue(value);
    onChange && onChange(value);
    onGetErrorFlg && onGetErrorFlg(errorFlag);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      dateFormats={{ monthAndYear: "YYYY年MM月" }}
      localeText={{
        previousMonth: "前月を表示",
        nextMonth: "次月を表示",
      }}
    >
      <DatePicker
        data-testid="Tkdatepicker"
        className="datepicker"
        ref={ref}
        {...rest}
        minDate={minDate ? dayjs(minDate) : undefined}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        label={label}
        format={format}
        value={value ? dayjs(value) : null}
        onChange={handleChange}
        disabled={disabled}
        slotProps={{
          textField: {
            error: error || !!errorMsg,
            helperText: errorMessage || errorMsg,
            variant: "outlined",
            size: "small",
            sx: {
              "& .MuiOutlinedInput-root": {
                "&.Mui-disabled fieldset": {
                  borderColor: "gray",
                },
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default TkDatePicker;
